import React from 'react';
import * as XLSX from 'xlsx';
import { Check, X, Wrench, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { navItems } from '../../components/layout/navConfig';
import { IT_SETTINGS_TABS } from '../../config/itSettingsTabs';

const LEVELS = { none: 0, view: 1, execute: 2, edit: 3 };

const roleCtx = (role) => ({
  isAdmin: role.id === 'role-admin',
  roleId: role.id,
  hasPermission: (area, level = 'view') =>
    (LEVELS[role.permissions?.[area] || 'none'] || 0) >= (LEVELS[level] || 0),
});

const isItemVisibleForRole = (item, role) => {
  if (role.id === 'role-admin') return true;
  if (item.adminOnly) return false;
  if (item.permArea) {
    const lvl = LEVELS[role.permissions?.[item.permArea] || 'none'] || 0;
    return lvl >= (LEVELS[item.permLevel || 'view'] || 0);
  }
  return true;
};

const exportToExcel = (roles) => {
  const header = ['Bereich', ...roles.map((r) => r.name)];
  const rows = [header];

  navItems.forEach((item) => {
    rows.push([item.label, ...roles.map((r) => (isItemVisibleForRole(item, r) ? 'Ja' : 'Nein'))]);
  });

  rows.push([]);
  rows.push(['IT-Einstellungen — Unterbereiche']);
  IT_SETTINGS_TABS.forEach((sub) => {
    rows.push([`  ${sub.label}`, ...roles.map((r) => (sub.can(roleCtx(r)) ? 'Ja' : 'Nein'))]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 32 }, ...roles.map(() => ({ wch: 20 }))];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sichtbarkeit');
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `Rollen-Sichtbarkeit_${stamp}.xlsx`);
};

export const VisibilityMatrixTab = ({ roles = [] }) => {
  if (!roles.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="visibility-empty">
        Keine Rollen vorhanden.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="visibility-matrix-tab">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Welche Sidebar-Punkte jede Rolle sieht. ✓ = sichtbar, ✗ = ausgeblendet. Standard-Punkte
          (ohne Berechtigungspflicht) sind für alle sichtbar.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 gap-2"
          onClick={() => exportToExcel(roles)}
          data-testid="visibility-export-btn"
        >
          <Download className="h-4 w-4" />
          Excel exportieren
        </Button>
      </div>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full border-collapse text-sm" data-testid="visibility-matrix-table">
          <thead>
            <tr className="bg-muted/50">
              <th className="sticky left-0 z-10 bg-muted/50 px-3 py-2 text-left font-medium whitespace-nowrap">
                Sidebar-Punkt
              </th>
              {roles.map((r) => (
                <th
                  key={r.id}
                  className="px-3 py-2 text-center font-medium whitespace-nowrap"
                  data-testid={`visibility-col-${r.id}`}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: r.color || '#6B7280' }}
                    />
                    {r.name}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {navItems.map((item) => (
              <tr key={item.path} className="border-t hover:bg-muted/30">
                <td className="sticky left-0 z-10 bg-background px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {item.icon && <item.icon className="h-4 w-4 text-muted-foreground" />}
                    <span>{item.label}</span>
                  </div>
                </td>
                {roles.map((r) => {
                  const visible = isItemVisibleForRole(item, r);
                  return (
                    <td
                      key={r.id}
                      className="px-3 py-2 text-center"
                      data-testid={`visibility-cell-${item.path.replace(/\//g, '')}-${r.id}`}
                    >
                      {visible ? (
                        <Check className="mx-auto h-4 w-4 text-green-600" />
                      ) : (
                        <X className="mx-auto h-4 w-4 text-muted-foreground/40" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}

            {/* IT-Einstellungen Untertabs */}
            <tr className="border-t bg-muted/40">
              <td
                className="sticky left-0 z-10 bg-muted/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                colSpan={roles.length + 1}
              >
                <div className="flex items-center gap-2">
                  <Wrench className="h-3.5 w-3.5" />
                  IT-Einstellungen — Unterbereiche
                </div>
              </td>
            </tr>
            {IT_SETTINGS_TABS.map((subTab) => (
              <tr key={`it-${subTab.value}`} className="border-t hover:bg-muted/30">
                <td className="sticky left-0 z-10 bg-background px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center gap-2 pl-5">
                    {subTab.icon && <subTab.icon className="h-4 w-4 text-muted-foreground" />}
                    <span className="text-muted-foreground">{subTab.label}</span>
                  </div>
                </td>
                {roles.map((r) => {
                  const visible = subTab.can(roleCtx(r));
                  return (
                    <td
                      key={r.id}
                      className="px-3 py-2 text-center"
                      data-testid={`visibility-cell-it-${subTab.value}-${r.id}`}
                    >
                      {visible ? (
                        <Check className="mx-auto h-4 w-4 text-green-600" />
                      ) : (
                        <X className="mx-auto h-4 w-4 text-muted-foreground/40" />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
